import React, { useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

const PayPalPurchase = ({ onClose, price, planId, isSubscription, language }) => {
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [orderId, setOrderId] = useState(null);

  // ✅ Define mapping safely without side effects
  const mapLocale = (lang) => {
    const supportedLocales = {
      en: "en_US",
      fr: "fr_FR",
      de: "de_DE",
      es: "es_ES",
      it: "it_IT",
      nl: "nl_NL",
      pl: "pl_PL",
      pt: "pt_PT",
      ru: "ru_RU",
      sv: "sv_SE",
      zh: "zh_CN",
      he: "he_IL",
      uk: "uk_UA"
    };

    if (typeof lang !== "string") return "en_US";
    const trimmed = lang.trim().toLowerCase();
    return supportedLocales[trimmed] || "en_US";
  };

  // ✅ Memoize to avoid recalculating on every render
  const locale = useMemo(() => mapLocale(language), [language]);

  const initialOptions = useMemo(() => ({
    "client-id": "AWwX3mwlz7F4u5RMDHjunCCEhoTLK25rOGXtNKw0DgcpsME9p4evZGRy13FDvxAHoJJ9HlovV9vWTy4U",
    currency: "USD",
    intent: isSubscription ? "subscription" : "capture",
    vault: isSubscription || undefined,
    "enable-funding": "card,venmo",
    components: "buttons",
    locale: locale
  }), [locale, isSubscription]);

  const handleCreateOrder = (data, actions) => {
    return actions.order
      .create({
        purchase_units: [{ amount: { currency_code: "USD", value: price } }],
      })
      .then((id) => {
        setOrderId(id);
        //console.log("Created order:", id);
        return id;
      });
  };

  const handleCreateSubscription = (data, actions) => {
    return actions.subscription
      .create({ plan_id: planId })
      .then((id) => {
        //console.log("Created subscription:", id);
        setSubscriptionId(id);
        return id;
      });
  };

  const handleApprove = async (data, actions) => {
    try {
      if (isSubscription) {
        const generalMessage = {
          ...generalMessageTemplate,
          messageType: "purchase_ok_pp",
          payments: [{
            productType: "subscription",
            amount: price,
            planId,
            payerId: data.payerID,
            subscriptionId,
            paymentOperator: "PayPal"
          }],
        };
        sendMessage(generalMessage);
      } else {
        const details = await actions.order.capture();
        //console.log("Capture success:", details);
        const generalMessage = {
          ...generalMessageTemplate,
          messageType: "purchase_ok_pp",
          payments: [{
            productType: "package",
            planId,
            amount: price,
            payPalDetails: details,
            orderId,
            paymentOperator: "PayPal"
          }],
        };
        sendMessage(generalMessage);
      }
      if (onClose) onClose();
    } catch (error) {
      //console.error("Error during PayPal transaction:", error);
    }
  };

  const handleError = (err) => {
    //console.error("PayPal button error:", err);
  };

  return (
    <div style={{ padding: 20, textAlign: "center" }}>
      <PayPalScriptProvider key={locale} options={initialOptions}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
          {...(isSubscription
            ? { createSubscription: handleCreateSubscription }
            : { createOrder: handleCreateOrder })}
          onApprove={handleApprove}
          onError={handleError}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalPurchase;
